import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapPin, Truck, Clock, ChevronRight, ArrowRight, ChevronDown, Star, Flame, Leaf, Shield, Utensils, Phone, Timer, Tag, CalendarDays, Users, Trophy, Store } from 'lucide-react';
import { useLocation } from '../contexts/LocationContext';
import { menuApi, discountApi, routingApi, locationApi } from '../config/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';
import NewsletterSignup from '../components/NewsletterSignup';
import AwardsSection from '../components/AwardsSection';
import { useScrollReveal } from '../hooks/useScrollReveal';

export default function Home() {
  const { selectedLocation, setShowLocationPicker, checkDeliveryZone, locations, plzRouting } = useLocation();
  const navigate = useNavigate();
  const [featuredItems, setFeaturedItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [postalCode, setPostalCode] = useState('');
  const [deliveryCheck, setDeliveryCheck] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const heroRef = useRef(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyBar(!entry.isIntersecting),
      { threshold: 0.1 }
    );
    if (heroRef.current) observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, []);

  const loadData = async () => {
    try {
      const locSlug = selectedLocation?.slug || locations?.[0]?.slug || 'rellingen';
      const [featuredRes, categoriesRes] = await Promise.all([
        menuApi.getFeatured(locSlug),
        menuApi.getCategories(locSlug),
      ]);
      setFeaturedItems(featuredRes.data.data || []);
      setCategories(categoriesRes.data.data || []);
    } catch (err) {
      console.error('Failed to load data:', err);
    }
  };

  const handlePostalCodeCheck = async () => {
    if (!postalCode || postalCode.length !== 5) return;
    setIsChecking(true);
    const result = await checkDeliveryZone(postalCode);
    setDeliveryCheck(result);
    setIsChecking(false);
  };

  // „Standort gewählt" gilt NUR mit gültigem PLZ-Routing — sonst wirkt Rellingen vorausgewählt
  const hasLocation = !!plzRouting && !!selectedLocation;
  const shopUrl = hasLocation ? `/bestellen/${selectedLocation.slug}` : '/bestellen';

  return (
    <div className="min-h-screen bg-[#0C0A09]">
      <SEO
        path="/"
        description={`ZOZO BURGER – Dein Premium Burger Restaurant${locations?.length ? ' in ' + locations.map(l => l.address?.city).filter(Boolean).join(' & ') : ''}. Frische Burger, knusprige Pizza, Wraps & mehr. Jetzt online bestellen!`}
      />
      <HeroSection
        ref={heroRef}
        hasLocation={hasLocation}
        selectedLocation={selectedLocation}
        setShowLocationPicker={() => navigate('/bestellen')}
        shopUrl={shopUrl}
        postalCode={postalCode}
        setPostalCode={setPostalCode}
        deliveryCheck={deliveryCheck}
        handlePostalCodeCheck={handlePostalCodeCheck}
        isChecking={isChecking}
      />
      <USPSection />
      <AnnouncementSection />
      {categories.length > 0 && <CategoriesSection categories={categories} />}
      {featuredItems.length > 0 && <BestsellerSection items={featuredItems} shopUrl={shopUrl} />}
      <DailyDealSection shopUrl={shopUrl} />
      <LocationsSection />
      <ReviewsSection />
      <AwardsSection />
      <NewsletterSection />
      <FAQSection />
      <CTASection shopUrl={shopUrl} hasLocation={hasLocation} setShowLocationPicker={() => navigate('/bestellen')} />

      {/* Mobile Sticky Bar */}
      <div
        className={`md:hidden fixed bottom-0 left-0 right-0 z-40 transition-all duration-300 ${showStickyBar ? 'translate-y-0' : 'translate-y-full'}`}
        style={{ backgroundColor: 'rgba(28,25,23,0.95)', backdropFilter: 'blur(20px)', borderTop: '1px solid rgba(168,28,28,0.2)' }}
        data-testid="landing-sticky-bar"
      >
        {hasLocation ? (
          <Link to={shopUrl} className="flex items-center justify-between px-5 py-4">
            <div>
              <span className="text-white font-semibold text-sm block">Jetzt bestellen</span>
              <span className="text-white/40 text-xs flex items-center gap-1"><Clock className="w-3 h-3" /> 30-45 Min Lieferzeit</span>
            </div>
            <span className="px-5 py-2.5 bg-[#A81C1C] text-white rounded-xl text-sm font-bold flex items-center gap-1.5 shadow-lg shadow-[#A81C1C]/20">
              Bestellen <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </Link>
        ) : (
          <button onClick={() => navigate('/bestellen')} className="flex items-center justify-between px-5 py-4 w-full">
            <span className="text-white font-semibold text-sm">Wo möchtest du bestellen?</span>
            <span className="px-5 py-2.5 bg-[#A81C1C] text-white rounded-xl text-sm font-bold flex items-center gap-1.5 shadow-lg shadow-[#A81C1C]/20">
              <MapPin className="w-3.5 h-3.5" /> Standort
            </span>
          </button>
        )}
      </div>
    </div>
  );
}

/* ======== SECTION 1: HERO WITH VIDEO BACKGROUND ======== */
const HeroSection = React.forwardRef(function HeroSection({
  hasLocation, selectedLocation, setShowLocationPicker, shopUrl,
  postalCode, setPostalCode, deliveryCheck, handlePostalCodeCheck, isChecking
}, ref) {
  const videoRef = useRef(null);

  const [videoFailed, setVideoFailed] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = 0.8;
    video.play().catch(() => setVideoFailed(true));
  }, []);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      {/* Video / Poster Background */}
      <div className="absolute inset-0">
        <img src="/images/hero-poster.jpg" alt="" className={`w-full h-full object-cover ${videoFailed ? '' : 'sm:hidden'}`} data-testid="hero-poster-mobile" />
        {!videoFailed && (
          <video
            ref={videoRef}
            autoPlay muted loop playsInline preload="auto"
            poster="/images/hero-poster.jpg"
            className="w-full h-full object-cover hidden sm:block"
            data-testid="hero-video"
            onError={() => setVideoFailed(true)}
          >
            <source src="/zozo-burger-hero.mp4" type="video/mp4" />
          </video>
        )}
        <div className="absolute inset-0 bg-[#0C0A09]/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0C0A09]/60 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0C0A09]/75 via-[#0C0A09]/35 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0C0A09] via-[#0C0A09]/20 to-transparent" />
      </div>

      {/* Hero Content */}
      <div className="relative z-10 container mx-auto px-4 md:px-6 pt-28 pb-24">
        <div className="max-w-2xl">
          <div className="flex items-center gap-4 mb-10">
            <img src="/images/logo-icon.png" alt="ZOZO Burger" className="w-16 h-16 md:w-20 md:h-20 object-contain drop-shadow-lg" data-testid="hero-logo" />
            <div>
              <span className="font-display text-3xl md:text-4xl font-bold text-white tracking-wide block leading-none drop-shadow-md">ZOZO</span>
              <span className="text-[10px] md:text-xs font-semibold tracking-[0.2em] text-white/50 uppercase mt-1 block">Burger &middot; Pizza &middot; Pasta & More</span>
            </div>
          </div>

          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[0.95] tracking-tight mb-6" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.5)' }}>
            ECHT. <span className="text-[#A81C1C]">FRISCH.</span><br />ZOZO.
          </h1>

          <p className="text-lg md:text-xl text-white/60 leading-relaxed mb-4 max-w-lg" style={{ textShadow: '0 1px 8px rgba(0,0,0,0.4)' }}>
            Frisch zubereitet. Direkt zu dir.
          </p>

          {/* ZOZO10 Promo */}
          <div className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 mb-6 px-4 py-3 rounded-xl border border-[#D4AF37]/40 bg-[#A81C1C]/25 backdrop-blur-md" data-testid="zozo10-hero-promo">
            <span className="font-mono font-bold text-[#D4AF37] text-base tracking-widest bg-black/30 px-2.5 py-1 rounded-md border border-[#D4AF37]/50">ZOZO10</span>
            <p className="text-white text-sm sm:text-base font-semibold">
              Dauerhaft <span className="text-[#D4AF37]">10% sparen</span> + <span className="text-[#D4AF37]">keine Lieferkosten</span>
            </p>
            <p className="w-full text-white/50 text-xs">Nur bei Bestellung über unsere Website · ab 15 € Bestellwert</p>
          </div>

          {/* 3 Lieferando-Auszeichnungen (Karten mit Lorbeerkranz) */}
          <div className="flex flex-wrap items-stretch gap-3 mb-8" data-testid="hero-awards">
            {[
              { year: '2024', title: 'Top bewertetes Restaurant', gold: false },
              { year: '2026', title: 'Top bewerteter Partner', gold: true },
              { year: '2023', title: 'Lieferando Award — Platz 3 Hamburg', gold: false },
            ].map((a) => (
              <div key={a.year}
                className="flex flex-col items-center text-center gap-1.5 px-4 py-3.5 rounded-2xl bg-black/55 backdrop-blur-md border border-[#D4AF37]/25 shadow-[0_4px_20px_rgba(0,0,0,0.35)] w-[150px] sm:w-[165px]"
                data-testid={`hero-award-${a.year}`}>
                <img src="/images/award-laurel.png" alt="" className="w-10 h-10 object-contain opacity-90" loading="lazy"
                  style={a.gold ? { filter: 'sepia(1) saturate(3) hue-rotate(-15deg) brightness(1.1)' } : undefined} />
                <span className="font-mono text-[#D4AF37] text-[11px] tracking-[0.3em]">{a.year}</span>
                <p className="font-display text-[13px] sm:text-sm text-white leading-snug">{a.title}</p>
                <span className="text-[9px] uppercase tracking-widest text-white/30 mt-auto pt-1">verliehen von Lieferando</span>
              </div>
            ))}
          </div>

          {hasLocation ? (
            <div className="space-y-4" data-testid="hero-cta-area">
              <div className="flex flex-wrap gap-3">
                <Link to={shopUrl}>
                  <Button className="h-14 px-10 bg-[#A81C1C] hover:bg-[#8B1616] text-white font-bold text-base rounded-xl transition-all duration-300 hover:shadow-[0_8px_24px_rgba(168,28,28,0.4)] hover:-translate-y-0.5 flex items-center gap-2.5 active:scale-[0.98]" data-testid="order-now-btn">
                    Jetzt bestellen <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Button onClick={() => setShowLocationPicker(true)} variant="outline" className="h-14 px-6 bg-white/[0.06] backdrop-blur-md border border-white/[0.12] text-white/80 hover:text-white hover:bg-white/[0.12] font-semibold text-base rounded-xl transition-all duration-300 active:scale-[0.98]" data-testid="change-location-btn">
                  <MapPin className="w-4 h-4 mr-2 text-[#A81C1C]" />
                  {selectedLocation.name?.replace('ZOZO Burger ', '')}
                </Button>
              </div>
              <div className="max-w-sm pt-2" data-testid="delivery-check-area">
                <label className="text-xs font-medium text-white/40 uppercase tracking-wider mb-2 block">Liefern wir zu dir?</label>
                <div className="flex gap-2">
                  <Input type="text" inputMode="numeric" placeholder="PLZ eingeben" value={postalCode} onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, '').slice(0, 5))} className="h-11 text-base bg-white/[0.06] backdrop-blur-sm border-white/[0.12] text-white placeholder:text-white/30 focus:border-[#A81C1C] focus:ring-[#A81C1C]/20 rounded-xl" data-testid="postal-code-input" />
                  <Button onClick={handlePostalCodeCheck} disabled={postalCode.length !== 5 || isChecking} variant="outline" className="h-11 px-5 bg-white/[0.06] backdrop-blur-sm border-white/[0.12] text-white/80 hover:text-white hover:bg-white/[0.12] font-medium rounded-xl transition-all" data-testid="check-delivery-btn">
                    {isChecking ? '...' : 'Prüfen'}
                  </Button>
                </div>
                {deliveryCheck && (
                  <div className={`mt-2 p-2.5 rounded-xl text-sm font-medium backdrop-blur-sm ${deliveryCheck.is_in_zone ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/15 text-red-400 border border-red-500/20'}`} data-testid="delivery-check-result">
                    {deliveryCheck.is_in_zone ? (
                      <span className="flex items-center gap-2"><Truck className="w-4 h-4" />Wir liefern zu dir! Ab EUR{deliveryCheck.minimum_order_value?.toFixed(2).replace('.',',')} MBW.</span>
                    ) : 'Leider liefern wir noch nicht in dein Gebiet.'}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <HeroBestellCard />
          )}
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce-slow">
        <span className="text-[10px] uppercase tracking-[0.2em] text-white/25 font-medium">Entdecken</span>
        <div className="w-px h-8 bg-gradient-to-b from-white/20 to-transparent" />
      </div>
    </section>
  );
});

/* ======== HERO: Call-a-Pizza-Stil Bestell-Einstieg (PLZ → Lieferung | Filialwahl) ======== */
function HeroBestellCard() {
  const navigate = useNavigate();
  const { applyRouting } = useLocation();
  const [plz, setPlz] = useState('');
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState('');

  const submit = async (value) => {
    if (!/^\d{5}$/.test(value)) { setErr('Bitte 5 Ziffern eingeben.'); return; }
    setBusy(true); setErr(''); setRes(null);
    try {
      const r = await routingApi.byPlz(value);
      const data = r.data;
      if (data.status === 'ungueltig') { setErr(data.meldung); return; }
      if (data.status === 'zugeordnet') {
        applyRouting({ plz: value, filiale_id: data.filiale.id, modus: 'lieferung' });
        navigate(`/bestellen/${data.filiale.id}`);
        return;
      }
      setRes(data); // geschlossen | kein_liefergebiet | eroeffnet_bald
    } catch {
      setErr('Verbindungsfehler – bitte erneut versuchen.');
    } finally {
      setBusy(false);
    }
  };

  const onChange = (e) => {
    const clean = e.target.value.replace(/\D/g, '').slice(0, 5);
    setPlz(clean); setErr(''); setRes(null);
    if (clean.length === 5) submit(clean);
  };

  return (
    <div className="max-w-md w-full" data-testid="hero-bestell-card">
      <div className="bg-black/40 backdrop-blur-xl border border-white/[0.14] rounded-2xl p-6 space-y-5 shadow-2xl">
        <div>
          <h3 className="font-display text-2xl font-black text-white uppercase tracking-tight">Einfach liefern lassen</h3>
          <p className="text-sm text-white/50 mt-1 mb-4">Gib deine Postleitzahl ein – wir finden deine ZOZO-Filiale.</p>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#E04040]" />
              <input
                type="text" inputMode="numeric" pattern="[0-9]*" maxLength={5}
                value={plz} onChange={onChange} placeholder="PLZ, z.B. 25462"
                className="w-full bg-white/[0.08] border-2 border-white/[0.15] focus:border-[#A81C1C] rounded-xl py-3 pl-10 pr-3 text-lg font-bold tracking-[0.2em] text-white placeholder:text-white/30 placeholder:tracking-normal placeholder:font-normal outline-none transition-colors"
                data-testid="hero-plz-input"
              />
            </div>
            <Button onClick={() => submit(plz)} disabled={plz.length !== 5 || busy}
              className="h-auto px-6 bg-[#A81C1C] hover:bg-[#8B1616] text-white font-bold rounded-xl flex items-center gap-2 active:scale-[0.98] disabled:opacity-50"
              data-testid="hero-lieferung-btn">
              <Truck className="w-4 h-4" /> {busy ? '...' : 'Lieferung'}
            </Button>
          </div>
          {err && <p className="text-sm text-red-400 mt-2" data-testid="hero-plz-error">{err}</p>}

          {res?.status === 'eroeffnet_bald' && (
            <div className="mt-3 p-3 rounded-xl bg-amber-500/15 border border-amber-500/30 text-sm text-amber-200" data-testid="hero-coming-soon">
              🎉 <b>{res.filiale?.name}</b> eröffnet <b>{res.eroeffnung_text}</b> — dann liefern wir auch zu dir!
            </div>
          )}
          {res?.status === 'kein_liefergebiet' && (
            <div className="mt-3 p-3 rounded-xl bg-white/[0.06] border border-white/[0.12] text-sm text-white/70" data-testid="hero-no-zone">
              Wir liefern noch nicht zu dir 😔 {res.naechste_filiale && <>Nächste Filiale: <b className="text-white">{res.naechste_filiale.name}</b>. </>}
              Du kannst gern <button onClick={() => navigate('/bestellen?abholung=1')} className="underline font-bold text-white">abholen</button>.
            </div>
          )}
          {res?.status === 'geschlossen' && (
            <div className="mt-3 p-3 rounded-xl bg-amber-500/15 border border-amber-500/30 text-sm text-amber-200" data-testid="hero-closed">
              <b>{res.filiale?.name}</b> hat gerade geschlossen.
              {res.vorbestellung_moeglich && (
                <button onClick={() => { applyRouting({ plz: res.plz, filiale_id: res.filiale.id, modus: 'lieferung' }); navigate(`/bestellen/${res.filiale.id}`); }}
                  className="block mt-2 underline font-bold text-white" data-testid="hero-preorder-btn">Jetzt vorbestellen →</button>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-white/[0.1] pt-4">
          <h4 className="font-display text-base font-bold text-white/90 uppercase tracking-tight mb-1">Filiale selbst auswählen</h4>
          <p className="text-xs text-white/40 mb-3">Zum Abholen oder wenn du deine ZOZO-Filiale schon kennst.</p>
          <Button onClick={() => navigate('/bestellen?abholung=1')} variant="outline"
            className="w-full h-12 bg-white/[0.06] border-white/[0.15] text-white/85 hover:text-white hover:bg-white/[0.12] font-bold rounded-xl active:scale-[0.98]"
            data-testid="hero-alle-filialen-btn">
            <Store className="w-4 h-4 mr-2 text-[#E04040]" /> Alle Filialen anzeigen
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ======== SECTION: Eröffnungs-Ankündigung (Filialen in Vorbereitung) ======== */
function AnnouncementSection() {
  const { announcements } = useLocation();
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  if (!announcements?.length) return null;
  const a = announcements[0];

  const signup = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(email)) { toast.error('Bitte gib eine gültige E-Mail-Adresse ein.'); return; }
    setSending(true);
    try {
      const res = await locationApi.eroeffnungInteresse(email, a.slug);
      toast.success(res.data?.message || 'Fast geschafft! Bitte bestätige deine E-Mail.');
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Das hat leider nicht geklappt.');
    }
    setSending(false);
  };

  return (
    <section className="py-6 md:py-10" data-testid="announcement-section">
      <div className="container mx-auto px-4 md:px-6">
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#1C1917] via-[#292524] to-[#1C1917] border border-amber-500/25 p-8 md:p-10">
          <div className="absolute -top-12 -right-12 w-48 h-48 bg-amber-500/10 rounded-full blur-3xl" />
          <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <div className="flex-1">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-500 text-black rounded-full text-xs font-black uppercase tracking-wider mb-4">
                <Star className="w-3.5 h-3.5" /> Neueröffnung
              </span>
              <h2 className="font-display text-2xl md:text-3xl font-black text-white tracking-tight" data-testid="announcement-title">
                Bald auch in {a.address?.city}!
              </h2>
              <p className="text-white/70 mt-2 text-base" data-testid="announcement-details">
                <b className="text-white">{a.name}</b> — {a.address?.street}, {a.address?.postal_code} {a.address?.city}
              </p>
              <p className="text-amber-400 font-bold mt-1" data-testid="announcement-opening">Eröffnung {a.eroeffnung_text}</p>
            </div>
            <div className="md:w-[360px]">
              {done ? (
                <div className="p-4 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-sm text-emerald-300" data-testid="announcement-signup-done">
                  ✓ Fast geschafft! Bitte bestätige deine E-Mail-Adresse über den Link, den wir dir geschickt haben.
                </div>
              ) : (
                <>
                  <p className="text-sm text-white/60 mb-2">Wir sagen dir Bescheid, sobald es losgeht:</p>
                  <div className="flex gap-2">
                    <Input type="email" placeholder="deine@email.de" value={email} onChange={(e) => setEmail(e.target.value)}
                      className="h-11 bg-white/[0.07] border-white/[0.15] text-white placeholder:text-white/30 rounded-xl"
                      data-testid="announcement-email-input" />
                    <Button onClick={signup} disabled={sending}
                      className="h-11 px-5 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-xl"
                      data-testid="announcement-signup-btn">
                      {sending ? '...' : 'Benachrichtigen'}
                    </Button>
                  </div>
                  <p className="text-[10px] text-white/35 mt-2">Double-Opt-In, DSGVO-konform. Abmeldung jederzeit über den Link in jeder E-Mail.</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ======== SECTION 2: USPs ======== */
function USPSection() {
  const { ref, isVisible } = useScrollReveal();
  const usps = [
    { icon: Flame, title: 'Täglich frisch', desc: 'Jeden Tag frische Zutaten, frisch zubereitet. Keine Kompromisse bei der Qualität.', accent: '#F97316' },
    { icon: Utensils, title: 'Über 100 Gerichte', desc: 'Burger, Pizza, Pasta, Wraps, Salate und vieles mehr. Für jeden Geschmack etwas dabei.', accent: '#F59E0B' },
    { icon: Truck, title: 'Schnelle Lieferung', desc: 'Lieferung in der Regel in 30-45 Minuten direkt an deine Tür. Oder Abholung in wenigen Minuten.', accent: '#10B981' },
    { icon: Shield, title: 'Große Halal-Auswahl', desc: 'Burger, Pizza, Chicken & mehr – Halal. Im Menü klar gekennzeichnet und filterbar.', accent: '#3B82F6' },
  ];

  return (
    <section ref={ref} className="py-14 md:py-18 relative overflow-hidden" data-testid="usp-section">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0C0A09] via-[#141210] to-[#0C0A09]" />
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className={`text-center mb-14 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <span className="text-xs font-semibold tracking-[0.25em] uppercase text-[#A81C1C] mb-3 block">Warum ZOZO?</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight">Qualität, die man schmeckt</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {usps.map((usp, i) => (
            <div key={i} className={`group relative p-6 rounded-2xl bg-white/[0.03] border border-white/[0.06] backdrop-blur-sm hover:bg-white/[0.06] hover:border-white/[0.12] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-lg ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: isVisible ? `${i * 120}ms` : '0ms' }} data-testid={`usp-${i}`}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: `${usp.accent}15` }}>
                <usp.icon className="w-5 h-5" style={{ color: usp.accent }} />
              </div>
              <h3 className="font-display text-base font-bold text-white mb-2">{usp.title}</h3>
              <p className="text-sm text-neutral-400 leading-relaxed">{usp.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ======== SECTION 3: CATEGORIES WITH REAL PRODUCT IMAGES ======== */
const categoryImages = {
  'burger': '/api/static/products/red_champion-burger-composed.webp',
  'smash-burger': '/api/static/products/red_smash-cheese-composed.webp',
  'pizza': '/api/static/products/red_dallas-composed.webp',
  'wraps': '/api/static/products/red_crunchy-chicken-wrap.webp',
  'pasta': '/api/static/products/red_pasta-cream-chicken-composed.webp',
  'fingerfood': '/api/static/products/red_chicken-wings-composed.webp',
  'vorspeisen-salate': '/api/static/products/red_caesar-salad-composed.webp',
  'getraenke': '/api/static/products/red_zozo-special-composed.webp',
  'pizzabroetchen': '/api/static/products/red_pb-salami-composed.webp',
  'kiddy-zone': '/api/static/products/red_chicken-nugget-burger-composed.webp',
  'dessert': '/api/static/products/red_american-zozo-brownie-composed.webp',
  'dips': '/api/static/products/red_bbq-sauce-composed.webp',
  'classics': '/api/static/products/red_rinder-currywurst-composed.webp',
};

function CategoriesSection({ categories }) {
  const { ref, isVisible } = useScrollReveal();
  const mainCats = categories.filter(c => c.item_count > 0).slice(0, 8);
  return (
    <section ref={ref} className="py-16 md:py-20 bg-[#0C0A09] relative" data-testid="categories-section">
      <div className="container mx-auto px-4 md:px-6">
        <div className={`flex items-end justify-between mb-10 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div>
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-[#A81C1C] mb-2 block">Unsere Karte</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight">Worauf hast du Lust?</h2>
          </div>
          <Link to="/bestellen" className="hidden md:flex items-center gap-1.5 text-sm font-medium text-white/40 hover:text-[#A81C1C] transition-colors group" data-testid="categories-view-all">
            Alle ansehen <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {mainCats.map((cat, i) => {
            const imgUrl = categoryImages[cat.slug] || categoryImages['burger'];
            return (
              <Link key={cat.id} to={`/bestellen?category=${cat.slug}`}
                className={`group relative h-44 md:h-52 rounded-2xl overflow-hidden border border-white/[0.06] hover:border-[#A81C1C]/40 transition-all duration-500 hover:shadow-[0_0_30px_-8px_rgba(168,28,28,0.25)] hover:-translate-y-1 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: isVisible ? `${i * 80}ms` : '0ms' }}
                data-testid={`category-${cat.slug}`}
              >
                {/* Vollbild-Produktfoto mit ZOZO-Rot-Hintergrund */}
                <div className="absolute inset-0 bg-[#1C1917]">
                  <img src={imgUrl} alt={cat.name}
                    className="object-cover absolute inset-0 w-full h-full group-hover:scale-110 transition-transform duration-700 ease-out"
                    loading="lazy"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0C0A09] via-[#0C0A09]/30 to-transparent group-hover:from-[#0C0A09]/95 transition-colors duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                  <h3 className="font-display text-base md:text-lg font-bold text-white tracking-tight group-hover:text-[#A81C1C] transition-colors duration-300">{cat.name}</h3>
                  {cat.slug === 'smash-burger' ? (
                    <span className="text-[10px] font-semibold text-amber-400/80 mt-0.5 block tracking-wide">Halal Dry Aged Beef</span>
                  ) : (
                    cat.item_count > 0 && <span className="text-xs text-white/30 mt-1 block">{cat.item_count} Gerichte</span>
                  )}
                </div>
                <div className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-white/[0.06] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:bg-[#A81C1C]/80">
                  <ArrowRight className="w-4 h-4 text-white" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ======== SECTION 4: BESTSELLER ======== */
function BestsellerSection({ items, shopUrl }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <section ref={ref} className="py-16 md:py-20 relative overflow-hidden" data-testid="bestseller-section">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0C0A09] via-[#141210] to-[#0C0A09]" />
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className={`flex items-end justify-between mb-10 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div>
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-[#A81C1C] mb-2 block">Am beliebtesten</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight">Das lieben unsere Kunden</h2>
          </div>
          <Link to={shopUrl} className="hidden md:flex items-center gap-1.5 text-sm font-medium text-white/40 hover:text-[#A81C1C] transition-colors group" data-testid="bestseller-view-all">
            Alle ansehen <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          {items.slice(0, 4).map((item, i) => {
            const price = item.pricing?.default?.price || item.pricing?.medium?.price || (item.pricing ? Object.values(item.pricing)[0]?.price : 0) || 0;
            const formattedPrice = price.toFixed(2).replace('.', ',');
            const isTransparent = item.image_url?.includes('-cutout.') || item.image_url?.includes('-freigestellt.');

            return (
              <Link key={item.id} to={`/bestellen?item=${item.id}`}
                className={`group relative bg-[#1C1917] border border-white/[0.06] rounded-2xl overflow-hidden hover:border-[#A81C1C]/30 transition-all duration-500 hover:shadow-[0_0_40px_-10px_rgba(168,28,28,0.25)] hover:-translate-y-1.5 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: isVisible ? `${i * 100}ms` : '0ms' }}
                data-testid={`bestseller-${item.slug}`}
              >
                {i === 0 && (
                  <div className="absolute top-3 left-3 z-20 px-2.5 py-1 bg-[#A81C1C] rounded-full text-[10px] font-bold text-white uppercase tracking-wider flex items-center gap-1 shadow-lg shadow-[#A81C1C]/30">
                    <Flame className="w-3 h-3" /> Beliebt
                  </div>
                )}
                <div className="relative overflow-hidden" style={{ height: '200px', background: isTransparent ? 'radial-gradient(ellipse at 50% 60%, #3D2B1F 0%, #2A1F17 40%, #1C1917 100%)' : '#1C1917' }}>
                  <img src={item.image_url || '/api/static/products/red_champion-burger-composed.webp'} alt={item.name}
                    className={`${isTransparent ? 'object-contain absolute inset-0 m-auto' : 'w-full h-full object-cover'} group-hover:scale-110 transition-transform duration-700 ease-out`}
                    style={isTransparent ? { width: '85%', height: '85%', filter: 'drop-shadow(0 8px 20px rgba(0,0,0,0.3))' } : undefined}
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-display text-base font-bold text-white tracking-tight line-clamp-1">{item.name}</h3>
                  <p className="text-xs text-white/30 line-clamp-1 mt-1">{item.description}</p>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/[0.06]">
                    <span className="font-bold text-sm text-[#A81C1C]">ab {formattedPrice} EUR</span>
                    <span className="text-xs font-semibold text-white/30 group-hover:text-[#A81C1C] transition-colors flex items-center gap-1">
                      Bestellen <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ======== SECTION 5: DYNAMIC DAILY DEAL WITH COUNTDOWN ======== */
const DAY_NAMES = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

function DailyDealSection({ shopUrl }) {
  const [todayDeal, setTodayDeal] = useState(null);
  const [countdown, setCountdown] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [loading, setLoading] = useState(true);
  const { ref, isVisible } = useScrollReveal();

  useEffect(() => { loadTodayDeal(); }, []);

  useEffect(() => {
    if (!todayDeal) return;
    const endTime = todayDeal.time_to || '22:45';
    const [endH, endM] = endTime.split(':').map(Number);
    const updateCountdown = () => {
      const now = new Date();
      const closing = new Date();
      closing.setHours(endH, endM, 0, 0);
      if (now >= closing) { setCountdown({ hours: 0, minutes: 0, seconds: 0 }); return; }
      const diff = closing - now;
      setCountdown({
        hours: Math.floor(diff / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
      });
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [todayDeal]);

  const loadTodayDeal = async () => {
    try {
      const res = await discountApi.getDailyDeals();
      const deals = res.data?.data || [];
      if (deals.length > 0) setTodayDeal(deals[0]);
    } catch { /* no deal today */ }
    setLoading(false);
  };

  if (loading) return null;
  const dayName = DAY_NAMES[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1];

  if (!todayDeal) return null;

  const hasTimeLeft = countdown.hours > 0 || countdown.minutes > 0 || countdown.seconds > 0;
  const endTimeDisplay = todayDeal.time_to || '22:45';

  return (
    <section ref={ref} className="py-6 md:py-10" data-testid="deal-section">
      <div className="container mx-auto px-4 md:px-6">
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#A81C1C] via-[#991B1B] to-[#7F1D1D] p-8 md:p-12 min-h-[360px]">
          <div className="absolute top-0 right-0 w-1/2 h-full opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)', backgroundSize: '24px 24px' }} />
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/5 rounded-full blur-3xl" />

          {/* Hero-Bild: rechts eingeblendet mit dunklem Farbverlauf zur linken Seite */}
          {todayDeal.image_url && (
            <div className="absolute inset-y-0 right-0 w-[60%] md:w-[55%] pointer-events-none" data-testid="deal-hero-image-wrap">
              <img
                src={todayDeal.image_url}
                alt={todayDeal.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#A81C1C] via-[#A81C1C]/70 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#7F1D1D]/60 via-transparent to-transparent" />
            </div>
          )}

          <div className="relative z-10 max-w-lg">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/15 rounded-full text-xs font-bold text-white uppercase tracking-wider">
                <Tag className="w-3.5 h-3.5" /> Heute: {dayName}
              </span>
              {todayDeal.show_countdown !== false && hasTimeLeft && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/10 rounded-full text-xs font-medium text-white/80">
                  <Timer className="w-3.5 h-3.5" />
                  Endet in {String(countdown.hours).padStart(2, '0')}:{String(countdown.minutes).padStart(2, '0')}:{String(countdown.seconds).padStart(2, '0')}
                </span>
              )}
            </div>

            <h2 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight mb-3" data-testid="deal-title">
              {todayDeal.display_text || todayDeal.title}
            </h2>
            <p className="text-white/80 text-lg leading-relaxed mb-6 max-w-lg" data-testid="deal-description">
              {todayDeal.description}
            </p>

            {todayDeal.discount_type === 'percentage' && (
              <div className="inline-flex items-center gap-3 px-5 py-3 bg-white/15 backdrop-blur-sm rounded-xl mb-8">
                <span className="font-display text-4xl md:text-5xl font-black text-white">-{todayDeal.discount_value}%</span>
                <div className="text-white/70 text-sm leading-tight"><span className="block">Nur heute</span><span className="block">bis {endTimeDisplay} Uhr</span></div>
              </div>
            )}
            {todayDeal.discount_type === 'fixed' && (
              <div className="inline-flex items-center gap-3 px-5 py-3 bg-white/15 backdrop-blur-sm rounded-xl mb-8">
                <span className="font-display text-4xl md:text-5xl font-black text-white">-{todayDeal.discount_value} EUR</span>
                <div className="text-white/70 text-sm leading-tight"><span className="block">Nur heute</span><span className="block">bis {endTimeDisplay} Uhr</span></div>
              </div>
            )}
            {todayDeal.discount_type === '2for1' && (
              <div className="inline-flex items-center gap-3 px-5 py-3 bg-white/15 backdrop-blur-sm rounded-xl mb-8">
                <span className="font-display text-3xl md:text-4xl font-black text-white">2 für 1</span>
                <div className="text-white/70 text-sm leading-tight"><span className="block">Zweites Gericht</span><span className="block">gratis</span></div>
              </div>
            )}
            {todayDeal.discount_type === 'double_points' && (
              <div className="inline-flex items-center gap-3 px-5 py-3 bg-white/15 backdrop-blur-sm rounded-xl mb-8" data-testid="deal-double-points-badge">
                <span className="font-display text-3xl md:text-4xl font-black text-white">2x Punkte</span>
                <div className="text-white/70 text-sm leading-tight"><span className="block">Doppelte ZOZO-Punkte</span><span className="block">auf jede Bestellung</span></div>
              </div>
            )}

            {todayDeal.show_countdown !== false && hasTimeLeft && (
              <div className="flex gap-2 mb-8" data-testid="deal-countdown">
                <CountdownBox value={countdown.hours} label="Std" />
                <span className="text-white/50 text-2xl font-bold self-center">:</span>
                <CountdownBox value={countdown.minutes} label="Min" />
                <span className="text-white/50 text-2xl font-bold self-center">:</span>
                <CountdownBox value={countdown.seconds} label="Sek" />
              </div>
            )}

            <Link to={shopUrl}>
              <Button className="h-12 px-8 bg-white text-[#A81C1C] font-bold text-base rounded-xl hover:bg-white/90 transition-all hover:-translate-y-px hover:shadow-lg flex items-center gap-2 active:scale-[0.98]" data-testid="deal-cta">
                Jetzt zuschlagen <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function CountdownBox({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/10">
        <span className="font-display text-2xl font-black text-white tabular-nums">{String(value).padStart(2, '0')}</span>
      </div>
      <span className="text-[10px] text-white/50 mt-1 uppercase tracking-wider font-medium">{label}</span>
    </div>
  );
}

/* ======== SECTION 6: LOCATIONS ======== */
function formatLocationHours(loc) {
  const hours = loc?.opening_hours;
  if (!hours) return '';
  const dayAbbr = { monday: 'Mo', tuesday: 'Di', wednesday: 'Mi', thursday: 'Do', friday: 'Fr', saturday: 'Sa', sunday: 'So' };
  const groups = {};
  Object.entries(dayAbbr).forEach(([key, abbr]) => {
    const day = hours[key];
    if (day && day.open && day.close) {
      const timeStr = `${day.open}-${day.close}`;
      if (!groups[timeStr]) groups[timeStr] = [];
      groups[timeStr].push(abbr);
    }
  });
  return Object.entries(groups).map(([time, days]) => {
    if (days.length > 2) return `${days[0]}-${days[days.length - 1]} ${time}`;
    return `${days.join(', ')} ${time}`;
  }).join(' | ');
}

function getNextSpecialDay(loc) {
  const specials = loc?.special_days || [];
  if (!specials.length) return null;
  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];
  const maxDate = new Date(now);
  maxDate.setDate(maxDate.getDate() + 7);
  const maxDateStr = maxDate.toISOString().split('T')[0];
  const upcoming = specials
    .filter(s => s.date >= todayStr && s.date <= maxDateStr)
    .sort((a, b) => a.date.localeCompare(b.date));
  if (!upcoming.length) return null;
  const next = upcoming[0];
  next._isToday = next.date === todayStr;
  return next;
}

function LocationsSection() {
  const { ref, isVisible } = useScrollReveal();
  const { locations, selectLocation } = useLocation();
  const navigate = useNavigate();

  return (
    <section ref={ref} className="py-14 md:py-18 relative overflow-hidden" data-testid="locations-section">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0C0A09] via-[#141210] to-[#0C0A09]" />
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className={`text-center mb-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <span className="text-xs font-semibold tracking-[0.25em] uppercase text-[#A81C1C] mb-2 block">In deiner Nähe</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight">Unsere Standorte</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
          {locations.slice(0, 3).map((location, i) => {
            const nextSpecial = getNextSpecialDay(location);
            return (
              <div key={location.slug} className={`group p-6 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-[#A81C1C]/20 transition-all duration-500 hover:-translate-y-1 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: isVisible ? `${i * 150}ms` : '0ms' }} data-testid={`location-card-${location.slug}`}>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-[#A81C1C]/10 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-[#A81C1C]/20 transition-colors">
                    <MapPin className="w-5 h-5 text-[#A81C1C]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-lg font-bold text-white mb-2.5">{location.name}</h3>
                    <div className="space-y-2 text-sm text-neutral-400">
                      <p>{location.address?.street}, {location.address?.postal_code} {location.address?.city}</p>
                      {location.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-3.5 h-3.5 text-neutral-500" />
                          <a href={`tel:${location.phone.replace(/\s/g, '')}`} className="hover:text-white transition-colors">{location.phone}</a>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5 text-neutral-500" />
                        <span className="text-xs">{formatLocationHours(location) || 'Keine Angabe'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {location.is_open_now ? (
                          <>
                            <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" /></span>
                            <span className="text-emerald-400 font-medium">Jetzt geöffnet</span>
                          </>
                        ) : (
                          <><span className="w-2.5 h-2.5 rounded-full bg-neutral-600" /><span className="text-neutral-500">Geschlossen</span></>
                        )}
                      </div>
                    </div>
                    {nextSpecial && (
                      <div className={`mt-3 flex items-start gap-2 p-2.5 rounded-xl ${nextSpecial._isToday ? 'bg-red-500/15 border border-red-500/20' : 'bg-amber-500/10 border border-amber-500/15'}`} data-testid={`special-day-notice-${location.slug}`}>
                        <CalendarDays className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${nextSpecial._isToday ? 'text-red-400' : 'text-amber-400'}`} />
                        <span className={`text-xs ${nextSpecial._isToday ? 'text-red-300 font-semibold' : 'text-amber-300'}`}>
                          {nextSpecial._isToday ? (
                            <>
                              <strong>HEUTE{nextSpecial.label ? ` (${nextSpecial.label})` : ''}:</strong>{' '}
                              {nextSpecial.is_closed ? 'Geschlossen' : `${nextSpecial.open}-${nextSpecial.close} Uhr`}
                              {nextSpecial.reason ? ` — ${nextSpecial.reason}` : ''}
                            </>
                          ) : (
                            <>
                              <strong>{nextSpecial.label || 'Sonderöffnungszeit'}:</strong>{' '}
                              {new Date(nextSpecial.date + 'T00:00:00').toLocaleDateString('de-DE', { day: 'numeric', month: 'long' })}
                              {nextSpecial.is_closed ? ' — Geschlossen' : ` — ${nextSpecial.open}-${nextSpecial.close} Uhr`}
                              {nextSpecial.reason ? ` (${nextSpecial.reason})` : ''}
                            </>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <Button onClick={() => { selectLocation(location); navigate(`/bestellen/${location.slug}`); }} className="w-full mt-5 h-11 bg-[#A81C1C] hover:bg-[#8B1616] text-white font-semibold rounded-xl transition-all hover:shadow-[0_4px_14px_0_rgba(168,28,28,0.35)] active:scale-[0.98]" data-testid={`select-location-${location.slug}`}>
                  Hier bestellen
                </Button>
                {location.google_review_url && (
                  <a href={location.google_review_url} target="_blank" rel="noreferrer"
                    className="mt-3 flex items-center justify-center gap-1.5 text-sm text-amber-400/90 hover:text-amber-300 transition-colors"
                    data-testid={`review-link-${location.slug}`}>
                    <span aria-hidden="true">⭐</span> Bewerte uns auf Google
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ======== SECTION 7: REVIEWS – Nur echter Google-Score, KEINE erfundenen Texte ======== */
function ReviewsSection() {
  const { ref, isVisible } = useScrollReveal();

  const googleMapsUrl = 'https://www.google.com/maps/place/ZOZO+Burger+Rellingen/';

  return (
    <section ref={ref} className="py-14 md:py-18 relative overflow-hidden" data-testid="reviews-section">
      <div className="absolute inset-0 bg-[#0C0A09]" />
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className={`max-w-xl mx-auto text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-xs font-semibold tracking-[0.25em] uppercase text-[#A81C1C] mb-6 block">Kundenstimmen</span>

          {/* Google Logo */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <span className="text-white/50 text-sm font-medium">Bewertungen auf Google</span>
          </div>

          {/* Stars + Score */}
          <div className="flex items-center justify-center gap-3 mb-3" data-testid="aggregate-rating">
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map(i => (
                <Star key={i} className="w-7 h-7 fill-amber-400 text-amber-400" />
              ))}
            </div>
          </div>
          <div className="mb-2">
            <span className="font-display text-5xl font-black text-white">4,8</span>
            <span className="text-white/40 text-lg font-medium ml-1">/ 5</span>
          </div>
          <p className="text-neutral-500 text-sm mb-8">Basierend auf echten Google-Bewertungen</p>

          {/* CTA to Google */}
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white/[0.06] border border-white/[0.1] rounded-xl text-white/70 hover:text-white hover:bg-white/[0.1] hover:border-white/[0.15] transition-all duration-300 text-sm font-medium group"
            data-testid="google-reviews-link"
          >
            Alle Bewertungen auf Google ansehen
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ======== SECTION 8: FAQ ======== */
function NewsletterSection() {
  const { ref, isVisible } = useScrollReveal();
  return (
    <section ref={ref} className="py-16 md:py-24 relative overflow-hidden" data-testid="newsletter-section">
      <div className="absolute inset-0 bg-[#0C0A09]" />
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(168,28,28,0.6) 1px, transparent 0)', backgroundSize: '28px 28px' }} />
      <div className={`container mx-auto px-4 md:px-6 max-w-2xl relative z-10 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="text-center mb-10">
          <span className="text-xs font-semibold tracking-[0.25em] uppercase text-[#A81C1C] mb-3 block">Newsletter</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight mb-3">Bekomm 5&nbsp;EUR geschenkt</h2>
          <p className="text-stone-400 text-sm md:text-base max-w-md mx-auto leading-relaxed">
            Abonniere unseren Newsletter und wir schicken dir deinen persönlichen Willkommensrabatt per Mail.
          </p>
        </div>
        <NewsletterSignup />
      </div>
    </section>
  );
}

function FAQSection() {
  const { ref, isVisible } = useScrollReveal();
  const faqs = [
    { q: 'Liefert ZOZO Burger nach Halstenbek, Schenefeld oder Pinneberg?', a: 'Ja! Wir liefern in einem großen Umkreis um unsere Standorte in Rellingen, Henstedt-Ulzburg und Pinneberg. Gib einfach deine PLZ auf unserer Bestellseite ein, um zu prüfen ob wir zu dir liefern.' },
    { q: 'Ist das Essen bei ZOZO Burger Halal?', a: 'Ja, viele unserer Gerichte sind Halal-zertifiziert. Du kannst im Menü einfach nach Halal-Optionen filtern und siehst sofort alle verfügbaren Gerichte.' },
    { q: 'Wie lange dauert die Lieferung?', a: 'In der Regel 30-45 Minuten ab Bestellung. Die genaue Lieferzeit hängt von deinem Standort und der aktuellen Auslastung ab.' },
    { q: 'Kann ich bei ZOZO Burger auch abholen?', a: 'Ja! Du kannst deine Bestellung auch zur Abholung aufgeben. Wahle einfach "Abholung" im Checkout und spare automatisch 10% mit dem Abholer-Rabatt.' },
    { q: 'Welche Zahlungsmethoden akzeptiert ZOZO Burger?', a: 'Wir akzeptieren PayPal und Barzahlung bei Lieferung oder Abholung. Weitere Zahlungsmethoden folgen bald.' },
    { q: 'Gibt es vegetarische oder vegane Optionen?', a: 'Ja, wir bieten mehrere vegetarische und vegane Gerichte an. Nutze die Filter im Menu, um gezielt danach zu suchen.' },
    { q: 'Wie kann ich Allergene einsehen?', a: 'Jedes Gericht hat eine Zutatenliste und Allergenkennzeichnung. Du findest diese Informationen direkt auf der Produktseite im Shop.' },
  ];

  const [openIdx, setOpenIdx] = useState(null);

  return (
    <section ref={ref} className="py-14 md:py-18 relative overflow-hidden" data-testid="faq-section">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0C0A09] via-[#141210] to-[#0C0A09]" />
      <div className="container mx-auto px-4 md:px-6 max-w-3xl relative z-10">
        <div className={`text-center mb-14 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <span className="text-xs font-semibold tracking-[0.25em] uppercase text-[#A81C1C] mb-3 block">Fragen & Antworten</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight">Häufige Fragen</h2>
        </div>
        <div className="space-y-3" itemScope itemType="https://schema.org/FAQPage">
          {faqs.map((faq, i) => (
            <div key={i} className={`rounded-2xl border border-white/[0.06] overflow-hidden bg-white/[0.02] hover:bg-white/[0.04] transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: isVisible ? `${i * 80}ms` : '0ms' }} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" data-testid={`faq-${i}`}>
              <button onClick={() => setOpenIdx(openIdx === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left group">
                <span className="font-semibold text-white pr-4 text-sm md:text-base" itemProp="name">{faq.q}</span>
                <div className={`w-8 h-8 rounded-lg bg-white/[0.05] flex items-center justify-center flex-shrink-0 group-hover:bg-[#A81C1C]/20 transition-colors`}>
                  <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform duration-300 ${openIdx === i ? 'rotate-180 text-[#A81C1C]' : ''}`} />
                </div>
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${openIdx === i ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-5 pb-5 text-neutral-400 leading-relaxed text-sm" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <span itemProp="text">{faq.a}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ======== SECTION 9: CTA ======== */
function CTASection({ shopUrl, hasLocation, setShowLocationPicker }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <section ref={ref} className="py-16 md:py-20 relative overflow-hidden" data-testid="cta-section">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0C0A09] via-[#1C1917] to-[#0C0A09]" />
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(168,28,28,0.5) 1px, transparent 0)', backgroundSize: '32px 32px' }} />
      <div className={`container mx-auto px-4 relative z-10 text-center max-w-2xl transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-6 scale-95'}`}>
        <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-5 tracking-tight leading-tight">
          Hunger? <span className="text-[#A81C1C]">Wir liefern.</span>
        </h2>
        <p className="text-white/45 text-base md:text-lg mb-10 leading-relaxed max-w-lg mx-auto">
          Bestelle jetzt online und genieße frische Burger, knusprige Pizza und mehr. Lieferung oder Abholung — du entscheidest.
        </p>
        {hasLocation ? (
          <Link to={shopUrl} className="inline-flex items-center gap-2.5 px-10 py-4 bg-[#A81C1C] hover:bg-[#8B1616] text-white font-bold text-lg rounded-xl transition-all duration-300 hover:shadow-[0_8px_30px_rgba(168,28,28,0.4)] hover:-translate-y-1 active:scale-[0.98]" data-testid="cta-order-btn">
            Jetzt bestellen <ArrowRight className="w-5 h-5" />
          </Link>
        ) : (
          <button onClick={() => setShowLocationPicker(true)} className="inline-flex items-center gap-2.5 px-10 py-4 bg-[#A81C1C] hover:bg-[#8B1616] text-white font-bold text-lg rounded-xl transition-all duration-300 hover:shadow-[0_8px_30px_rgba(168,28,28,0.4)] hover:-translate-y-1 active:scale-[0.98]" data-testid="cta-location-btn">
            <MapPin className="w-5 h-5" /> Standort wählen
          </button>
        )}
      </div>
    </section>
  );
}
